import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import { safeFromUnixTime, shortFormatDate, timeOnlyDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { safelyRender } from '@liveauctioneers/caterwaul-components/lib/utils/safelyRender';
import { Paperclip, User } from '@liveauctioneers/hammer-ui-core/icons';
import { BodySecondary, Caption, H5, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { isToday, isYesterday } from 'date-fns';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ConversationType } from '../types/Conversation';

type Props = {
    readonly getMessengerUrl: Function;
    readonly handleSelect: (e: React.MouseEvent<HTMLInputElement>) => void;
    readonly mobile?: boolean;
    readonly selected: boolean;
    testid?: string;
    readonly thread: ConversationType;
    readonly yesterdayText?: React.ReactNode;
};

const ListItem = ({
    getMessengerUrl,
    handleSelect,
    mobile,
    selected,
    testid = 'messengerListItem',
    thread,
    yesterdayText = 'Yesterday',
}: Props) => {
    const navigate = useNavigate();
    const {
        conversationId,
        hasAttachments,
        imageUrl,
        logoUrl,
        messageCount,
        read,
        sender,
        subject,
        tsLastMessageSent,
        type,
    } = thread;
    const messengerChatUrl = getMessengerUrl(conversationId);
    const messageSentDate = safeFromUnixTime(tsLastMessageSent);
    let date: React.ReactNode = shortFormatDate(tsLastMessageSent);

    if (isToday(messageSentDate)) {
        date = timeOnlyDate(tsLastMessageSent);
    } else if (isYesterday(messageSentDate)) {
        date = yesterdayText;
    }

    let avatar;

    if (imageUrl) {
        avatar = (
            <ThreadImageContainer>
                <img
                    loading="lazy"
                    src={imageUrl}
                />
            </ThreadImageContainer>
        );
    } else if (type === 'bidder') {
        avatar = (
            <StyledAvatar>
                <User
                    size="md"
                    style="fill"
                />
            </StyledAvatar>
        );
    } else if (type === 'house') {
        avatar = (
            <ThreadImageContainer>
                <img
                    loading="lazy"
                    src={logoUrl}
                />
            </ThreadImageContainer>
        );
    }

    return (
        <StyledListItem
            $read={read}
            data-testid={testid}
            onClick={() => navigate(messengerChatUrl)}
            role="button"
        >
            {!mobile && (
                <td>
                    <StyledCheckbox
                        checked={selected}
                        label={`Select ${sender} - Subject: ${subject}`}
                        labelDisplay=""
                        name={`${conversationId}`}
                        onClick={handleSelect}
                    />
                </td>
            )}
            <MessageDetailsCell>
                <MessageDetailsCellContainer>
                    {avatar}
                    <MessageDetails>
                        <SubjectText>
                            {!read ? (
                                <H5>{safelyRender({ html: sender, wrapper: 'span' })}</H5>
                            ) : (
                                <BodySecondary color="secondary">
                                    {safelyRender({ html: sender, wrapper: 'span' })}
                                </BodySecondary>
                            )}
                            {messageCount > 1 && (
                                <Caption
                                    className={cn('mb-[1px] ml-[3px] self-end', !read && 'hui-text-label-strong')}
                                    color={read ? 'secondary' : undefined}
                                >
                                    &nbsp;{messageCount}
                                </Caption>
                            )}
                        </SubjectText>
                        {!read ? (
                            <H6 color="secondary">{safelyRender({ html: subject, wrapper: 'span' })}</H6>
                        ) : (
                            <BodySecondary color="secondary">
                                {safelyRender({ html: subject, wrapper: 'span' })}
                            </BodySecondary>
                        )}
                    </MessageDetails>
                </MessageDetailsCellContainer>
            </MessageDetailsCell>

            <AttachmentCell>{hasAttachments && <Paperclip size="sm" />}</AttachmentCell>
            {!mobile && (
                <MessageDateCell>
                    <DateText>
                        {!read ? (
                            <H6 color="secondary">{date}</H6>
                        ) : (
                            <BodySecondary color="secondary">{date}</BodySecondary>
                        )}
                    </DateText>
                </MessageDateCell>
            )}
        </StyledListItem>
    );
};

export default React.memo<Props>(ListItem);

const DateText = styled.div`
    margin-left: 20px;
    text-align: end;
    @media (max-width: 699px) {
        display: none !important;
    }
`;

const StyledAvatar = styled.div<{ $mobile?: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: ${({ theme }) => theme.colors.blue100};
    margin-right: ${({ $mobile }) => ($mobile ? '15px' : '20px')};
    align-self: center;
    position: relative;
    overflow: hidden;

    && {
        svg {
            color: ${({ theme }) => theme.colors.white};
            position: absolute;
            bottom: -4px;
        }
    }
`;

const SubjectText = styled.div`
    && {
        display: flex;
        flex-direction: row;
    }
`;

const StyledListItem = styled.tr<{ $read?: boolean }>`
    cursor: pointer;
    background-color: ${({ $read, theme }) => (!$read ? theme.colors.white : theme.colors.grey500)};
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};

    &:hover {
        ${({ theme }) => css`
            && {
                border-top: 1px solid ${theme.colors.blue200};
                border-bottom: 1px solid ${theme.colors.blue200};
                td:first-of-type {
                    border-left: 1px solid ${theme.colors.blue200};
                }
                td:last-of-type {
                    border-right: 1px solid ${theme.colors.blue200};
                }
            }
        `}
    }

    &:last-child {
        border-bottom: 1px solid transparent;
    }

    && {
        td {
            border-top: none;
            border-bottom: none;
        }
    }
`;

const StyledCheckbox = styled(Checkbox)`
    margin: 0;
    padding: 0;
    left: 8px;

    @media (max-width: 699px) {
        display: none;
    }
`;

const ThreadImageContainer = styled.div`
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: center;
    position: relative;
    margin-right: 20px;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.white};

    img {
        max-height: 30px;
        max-width: 30px;
    }
`;

const MessageDetailsCell = styled.td``;

const MessageDetailsCellContainer = styled.div`
    display: flex;
    flex-direction: row;
`;

const MessageDetails = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-decoration: none;

    h6 {
        display: flex;
    }

    & > span {
        display: block;
    }

    &:hover {
        text-decoration: none;
    }
`;

const MessageDateCell = styled.td`
    && {
        display: table-cell;
        align-items: center;
        white-space: nowrap;
        padding: 6px 10px 6px 6px;
    }
`;
const AttachmentCell = styled.td`
    display: table-cell;
`;
